import React, { useState } from "react";
import { Alert, Snackbar } from "@mui/material";
import {
	CustomButton,
	CustomDialog,
	CustomIconButton,
	CustomTextField,
	icon,
	IconComponent,
	SidePanel,
} from "../../../../common/components";
import { Permissions, translate } from "../../../../common/providers";
import styles from "./AddUserSidePanel.module.css";

export default function AddUserSidePanel({ open, onActive = null, onAdd, onClose }) {
	const [payload, setPayload] = useState({
		firstname: "",
		lastname: "",
		email: "",
		role: Permissions.COMPANY_MEMBER.role,
	});
	const [errors, setErrors] = useState({ firstname: false, lastname: false, email: false });
	const [showWarningModal, setShowWarningModal] = useState(false);
	const [showToast, setShowToast] = useState(false);

	const onChange = (value, field) => {
		setErrors((prev) => ({ ...prev, [field]: false }));
		setPayload((prev) => ({ ...prev, [field]: value }));
	};

	const reset = () => {
		setPayload({ firstname: "", lastname: "", email: "", role: Permissions.COMPANY_MEMBER.role });
		setErrors({ firstname: false, lastname: false, email: false });
	};

	const isEmail = (email) => /^[a-zA-Z0-9+_.-]+@[\w\d.-]+\.[\w\d.-]+/.test(email);

	const onSubmit = () => {
		const errs = {
			firstname: !(payload.firstname && payload.firstname.length > 2),
			lastname: !(payload.lastname && payload.lastname.length > 2),
			email: !isEmail(payload.email),
		};
		const hasError = Object.keys(errs).some((key) => errs[key]);
		if (!hasError) {
			onAdd({ payload });
			reset();
			onClose();
			setShowWarningModal(true);
		}
		setErrors(errs);
	};

	const handleModalConfirm = () => {
		setShowWarningModal(false);
		setShowToast(true);
	};

	return (
		<>
			<SidePanel open={open} size={38} onActive={onActive} onClose={onClose}>
				<div className={styles.panel}>
					<div className={styles.panel__header}>
						<span className={styles.panel__title}>{translate("admin.add-user.title")}</span>
						<CustomIconButton icon={icon.faTimes} onClick={onClose} />
					</div>
					<div className={styles.panel__content}>
						<Alert className={styles.alert} severity="info">
							{translate("admin.add-user.warning")}
						</Alert>
						<div className={styles.form}>
							<div className={styles.field}>
								<span className={styles.field__label}>
									{translate("admin.user-details.text-field.label.first-name")}
									<span className={styles.field__required}>*</span>
								</span>
								<CustomTextField
									required
									data-testid="user.first.name"
									error={errors.firstname}
									helperText={
										errors.firstname &&
										translate("common:error.required-with-min-data", { min: "3" })
									}
									placeholder={translate("admin.add-user.placeholder")}
									size="small"
									value={payload.firstname || ""}
									variant="outlined"
									onChange={(e) => onChange(e.target.value, "firstname")}
								/>
							</div>
							<div className={styles.field}>
								<span className={styles.field__label}>
									{translate("admin.user-details.text-field.label.last-name")}
									<span className={styles.field__required}>*</span>
								</span>
								<CustomTextField
									required
									data-testid="user.last.name"
									error={errors.lastname}
									helperText={
										errors.lastname &&
										translate("common:error.required-with-min-data", { min: "3" })
									}
									placeholder={translate("admin.add-user.placeholder")}
									size="small"
									value={payload.lastname || ""}
									variant="outlined"
									onChange={(e) => onChange(e.target.value, "lastname")}
								/>
							</div>
							<div className={styles.field}>
								<span className={styles.field__label}>
									{translate("admin.user-details.text-field.label.email")}
									<span className={styles.field__required}>*</span>
								</span>
								<CustomTextField
									required
									data-testid="user.email"
									error={errors.email}
									helperText={
										errors.email && translate("common:multiple-email-input.error-message.not-valid")
									}
									placeholder={translate("admin.add-user.placeholder")}
									size="small"
									value={payload.email || ""}
									variant="outlined"
									onChange={(e) => onChange(e.target.value, "email")}
								/>
							</div>
						</div>
						<CustomButton color="primary" fullWidth={false} variant="contained" onClick={onSubmit}>
							{translate("admin.add-user.submit")}
						</CustomButton>
					</div>
				</div>
			</SidePanel>
			<CustomDialog
				hideCloseButton
				open={showWarningModal}
				submitLabel={translate("admin.add-user.modal.confirm")}
				title={translate("admin.add-user.modal.title")}
				onClose={handleModalConfirm}
				onSubmit={handleModalConfirm}
			>
				{translate("admin.add-user.modal.content")}
			</CustomDialog>
			<Snackbar
				anchorOrigin={{ vertical: "top", horizontal: "right" }}
				autoHideDuration={4000}
				open={showToast}
				onClose={() => setShowToast(false)}
			>
				<Alert
					icon={<IconComponent color="currentColor" icon={icon.faCheckCircle} size="sm" />}
					severity="success"
					onClose={() => setShowToast(false)}
				>
					{translate("admin.add-user.toast.success")}
				</Alert>
			</Snackbar>
		</>
	);
}
